import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { getArticleBySlug, allArticles } from '@/mocks/articles';
import { kaspreadsheetUrl } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gtbuyspreadsheet.pro';

function buildArticleJsonLd(article: ReturnType<typeof getArticleBySlug>) {
  if (!article) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: 'GTBuy Spreadsheet',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d3e1.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/guide/${article.slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : getArticleBySlug('gtbuy-sheet-guide');

  if (!article) {
    return <NotFound />;
  }

  const articleJsonLd = buildArticleJsonLd(article);
  const pageUrl = `${siteUrl}/guide/${article.slug}`;

  return (
    <>
      {articleJsonLd && <JsonLd data={articleJsonLd} />}
      <title>{article.title}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content="gtbuy spreadsheet, sourcing spreadsheet, reseller tools, order management, ecommerce guide" />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={article.imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.imageUrl} />

      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="max-w-4xl mx-auto px-4 md:px-6 lg:px-10">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
              Home
            </Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-600 font-medium whitespace-nowrap">{article.category}</span>
          </nav>

          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-xs font-semibold text-primary-700 uppercase tracking-wider mb-5 whitespace-nowrap">
            {article.category}
          </span>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold font-heading text-foreground-950 leading-tight mb-6">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={article.authorAvatar}
                  alt={article.authorName}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <p className="font-semibold text-foreground-800 text-sm">{article.authorName}</p>
                <p className="text-xs text-foreground-400">{article.authorRole}</p>
              </div>
            </div>
            <span className="text-foreground-300">&middot;</span>
            <span>{article.publishDate}</span>
          </div>

          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
          </div>

          <div
            className="prose-content text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 p-6 rounded-2xl bg-background-100 border border-background-200/60">
            <p className="text-sm text-foreground-600 mb-3">
              <strong className="text-foreground-900">Explore More:</strong> Ready to put your knowledge into action?
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-full transition-colors duration-200 whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-home-line text-sm"></i>
                </span>
                Back to Homepage
              </Link>
              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-foreground-700 bg-white hover:bg-background-50 border border-background-200 rounded-full transition-colors duration-200 whitespace-nowrap"
              >
                Browse Products on KA Spreadsheet
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-xl font-bold font-heading text-foreground-900 mb-6">Related Guides</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {allArticles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/guide/${related.slug}`}
                    className="group flex items-start gap-4 p-4 rounded-xl bg-white border border-background-200/60 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={related.imageUrl} alt={related.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold text-primary-500 uppercase tracking-wider">{related.category}</span>
                      <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mt-0.5">{related.title}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </article>
      </main>

      <style>{`
        .prose-content h2 {
          font-family: var(--font-heading);
          font-size: 1.75rem;
          font-weight: 800;
          color: oklch(var(--foreground-950));
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          line-height: 1.25;
        }
        .prose-content h3 {
          font-family: var(--font-heading);
          font-size: 1.3rem;
          font-weight: 700;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }
        .prose-content p {
          font-size: 1.05rem;
          line-height: 1.8;
          margin-bottom: 1.25rem;
          color: oklch(var(--foreground-700));
        }
        .prose-content strong {
          color: oklch(var(--foreground-900));
          font-weight: 600;
        }
        .prose-content ul {
          list-style: disc;
          padding-left: 1.5rem;
          margin-bottom: 1.25rem;
        }
        .prose-content li {
          font-size: 1.05rem;
          line-height: 1.8;
          margin-bottom: 0.5rem;
          color: oklch(var(--foreground-700));
        }
        .prose-content a {
          color: oklch(var(--primary-600));
          text-decoration: underline;
          text-underline-offset: 2px;
          font-weight: 500;
          transition: color 0.2s;
        }
        .prose-content a:hover {
          color: oklch(var(--primary-700));
        }
        @media (max-width: 768px) {
          .prose-content h2 {
            font-size: 1.4rem;
          }
          .prose-content h3 {
            font-size: 1.15rem;
          }
          .prose-content p, .prose-content li {
            font-size: 1rem;
          }
        }
      `}</style>

      <Footer />
    </>
  );
}